import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { X } from 'lucide-react';
import { useLocation } from 'react-router-dom';

const API_URL = '';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95) translateY(10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 3000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 16px;
  padding-bottom: 80px;

  @media (min-width: 1024px) {
    display: none;
  }
`;

const PopupCard = styled.div`
  position: relative;
  background: ${({ theme }) => theme.isDarkMode ? '#1a1b1e' : '#FFFFFF'};
  border-radius: 16px;
  width: 100%;
  max-width: 400px;
  padding: 24px 20px 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  animation: ${fadeIn} 0.3s ease-out;
  max-height: 85vh;
  overflow-y: auto;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: ${({ theme }) => theme.isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  z-index: 1;

  &:hover {
    background: ${({ theme }) => theme.isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'};
  }
`;

const AdLabel = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.isDarkMode ? '#FFFFFF' : '#18191B'};
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.5px;
  display: block;
  margin-bottom: 8px;
`;

const AdImage = styled.img`
  width: 100%;
  border-radius: 12px;
  margin-bottom: 12px;
  object-fit: cover;
  max-height: 200px;
`;

const AdTitle = styled.h3`
  font-size: 17px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 8px 0;
  padding-right: 28px;
`;

const AdDescription = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.5;
  white-space: pre-wrap;
  margin: 0 0 16px 0;

  strong {
    font-weight: 700;
    color: ${({ theme }) => theme.colors.text};
  }

  em {
    font-style: italic;
  }

  u {
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

const AdButton = styled.button`
  background: ${({ theme }) => theme.isDarkMode ? '#FFFFFF' : '#18191B'};
  color: ${({ theme }) => theme.isDarkMode ? '#18191B' : '#EFF3F7'};
  border: none;
  padding: 10px 24px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  width: 100%;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: ${({ theme }) => theme.isDarkMode ? '#EFF3F7' : '#2a2d30'};
  }
`;

const StaticAdContent = () => (
  <>
    <AdLabel>Что нового?</AdLabel>
    <AdTitle>ПОЛКА в сети!</AdTitle>
    <AdDescription>
      УРА! Это действительно произошло. Спасибо вам, что верили в Полку и ждали.
    </AdDescription>
    <AdButton onClick={() => {}}>Класс</AdButton>
  </>
);

const escapeHtml = (value = '') =>
  String(value)
    .replace(/&/g, '&')
    .replace(/</g, '<')
    .replace(/>/g, '>')
    .replace(/"/g, '"')
    .replace(/'/g, '&#039;');

const formatAdDescription = (rawText = '') => {
  if (!rawText) return '';
  let text = escapeHtml(rawText);

  text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  text = text.replace(/\*(.+?)\*/g, '<em>$1</em>');
  text = text.replace(/__(.+?)__/g, '<u>$1</u>');

  return text;
};

const MOBILE_AD_POPUP_KEY = 'polka_mobile_ad_popup_closed';

const MobileAdPopup = () => {
  const { pathname } = useLocation();
  const isAdminPage = pathname === '/adminchek' || pathname.startsWith('/adminchek/');
  const isConnectionPage = pathname === '/connect';
  const [ad, setAd] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Проверка localStorage: показывать или нет
  const shouldShow = useCallback(() => {
    try {
      const stored = localStorage.getItem(MOBILE_AD_POPUP_KEY);
      if (!stored) return true;

      const { timestamp } = JSON.parse(stored);
      const now = Date.now();
      const hoursPassed = (now - timestamp) / (1000 * 60 * 60);
      return hoursPassed >= 24;
    } catch {
      return true;
    }
  }, []);

  // Загрузка рекламы
  useEffect(() => {
    if (!isMobile || isAdminPage || isConnectionPage) return;
    if (!shouldShow()) return;

    const loadAd = async () => {
      try {
        const res = await fetch(`${API_URL}/api/ads/random`);
        if (res.ok) {
          const data = await res.json();
          setAd(data);
          setVisible(true);
        } else {
          // Если нет рекламы с сервера, показываем статичную
          setAd(null);
          setVisible(true);
        }
      } catch {
        // При ошибке показываем статичную рекламу
        setAd(null);
        setVisible(true);
      }
    };
    loadAd();
  }, [isMobile, shouldShow, isAdminPage, isConnectionPage]);

  const handleClose = () => {
    setVisible(false);
    try {
      localStorage.setItem(
        MOBILE_AD_POPUP_KEY,
        JSON.stringify({ timestamp: Date.now() })
      );
    } catch (err) {
      console.error('Failed to save popup timestamp:', err);
    }
  };

  const handleAdClick = async (adItem) => {
    try {
      const token = localStorage.getItem('polka_token');
      let userPayload = {};
      if (token) {
        try {
          const base64Url = token.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          userPayload = JSON.parse(window.atob(base64));
        } catch {
          // ignore
        }
      }
      await fetch(`${API_URL}/api/ads/${adItem.id}/click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userPayload.id || null,
          user_email: userPayload.email || ''
        })
      });
      if (adItem.button_link) {
        window.open(adItem.button_link, '_blank', 'noopener,noreferrer');
      }
      handleClose();
    } catch (err) {
      console.error('Click error:', err);
    }
  };

  if (!visible || !isMobile || isAdminPage || isConnectionPage) return null;

  return (
    <Overlay onClick={handleClose}>
      <PopupCard onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={handleClose}>
          <X size={18} />
        </CloseButton>

        {ad ? (
          <>
            <AdLabel>{ad.label || 'Реклама'}</AdLabel>
            {ad.type === 'image' && ad.image_path && (
              <AdImage
                src={`${API_URL}${ad.image_path}`}
                alt={ad.title || ''}
              />
            )}
            {ad.title && <AdTitle>{ad.title}</AdTitle>}
            {ad.description && (
              <AdDescription
                dangerouslySetInnerHTML={{ __html: formatAdDescription(ad.description) }}
              />
            )}
            {ad.button_text && (
              <AdButton onClick={() => handleAdClick(ad)}>
                {ad.button_text}
              </AdButton>
            )}
          </>
        ) : (
          <StaticAdContent />
        )}
      </PopupCard>
    </Overlay>
  );
};

export default MobileAdPopup;
